import React from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';

const formatNumber = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);

// Label dentro da barra: mostra o valor sempre; se a barra for baixa, coloca em cima
function RenderBarLabel(props) {
  const { x, y, width, height, value } = props;
  const showInside = height >= 20;
  const textY = showInside ? y + height / 2 : y - 5;
  return (
    <text
      x={x + width / 2}
      y={textY}
      fill={showInside ? '#fff' : '#333'}
      textAnchor="middle"
      dominantBaseline="middle"
      style={{ fontWeight: 600, fontSize: showInside ? 11 : 10 }}
    >
      {value != null && value !== '' ? formatNumber(value) : '0'}
    </text>
  );
}

const anosDisponiveis = [2024, 2025, 2026, 2027];

export default function EvolucaoSolicitacoesChart({ dados, loading, ano, onAnoChange }) {
  if (loading) return null;
  if (!dados?.meses?.length) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.2)', textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">Dados de evolução por solicitações (12 meses) não disponíveis.</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid rgba(25,118,210,0.2)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
        boxShadow: '0 4px 24px rgba(25,118,210,0.08)'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary">
            Evolução por Solicitações – {ano || new Date().getFullYear()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contratação, Aditivo e Notificação por mês.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Ano</InputLabel>
          <Select
            value={ano || new Date().getFullYear()}
            label="Ano"
            onChange={(e) => onAnoChange?.(e.target.value)}
          >
            {anosDisponiveis.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ width: '100%', height: 360 }}>
        <ResponsiveContainer>
          <BarChart
            data={dados.meses}
            margin={{ top: 24, right: 16, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#546e7a' }}
              axisLine={{ stroke: 'rgba(0,0,0,0.12)' }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#546e7a' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatNumber}
            />
            <Tooltip
              formatter={(value, name) => [
                formatNumber(value),
                name === 'contratacao' ? 'Contratação' : name === 'aditivo' ? 'Aditivo' : name === 'notificacao' ? 'Notificação' : name
              ]}
              labelFormatter={(label) => `Mês: ${label}`}
              contentStyle={{
                borderRadius: 12,
                border: '1px solid rgba(25,118,210,0.2)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: 16 }}
              formatter={(v) => (v === 'contratacao' ? 'Contratação' : v === 'aditivo' ? 'Aditivo' : v === 'notificacao' ? 'Notificação' : v)}
              iconType="square"
              iconSize={10}
            />
            <Bar dataKey="contratacao" name="contratacao" fill="#1976d2" radius={[4, 4, 0, 0]} maxBarSize={32}>
              <LabelList dataKey="contratacao" content={<RenderBarLabel />} />
            </Bar>
            <Bar dataKey="aditivo" name="aditivo" fill="#7b1fa2" radius={[4, 4, 0, 0]} maxBarSize={32}>
              <LabelList dataKey="aditivo" content={<RenderBarLabel />} />
            </Bar>
            <Bar dataKey="notificacao" name="notificacao" fill="#2e7d32" radius={[4, 4, 0, 0]} maxBarSize={32}>
              <LabelList dataKey="notificacao" content={<RenderBarLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
